import { ApplicationRef, Component, NgZone, OnDestroy } from '@angular/core'
import { Route, Router, NavigationEnd, NavigationError, ActivatedRoute } from '@angular/router'
import { SessionWarningDialogComponent } from './sessionWarningDialog/session-warning-dialog.component';
import { IdleService } from './idle.service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { PortalGlobals } from './portalglobals';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'app',
    template: `
        <router-outlet></router-outlet>
        <app-session-warning-dialog
          [isOpen]="showModal"
          [countdown]="countdown"
          (stay)="onStay()"
          (logout)="onLogout()">
        </app-session-warning-dialog>
    `
})
export class AppComponent implements OnDestroy {
    showModal = false;
    countdown = 0;
    private readonly WARNING_AFTER_SECONDS = 27 * 60;
    private readonly WARNING_COUNTDOWN_SECONDS = 3 * 60;
    private idleTrackingEnabled = false;
    private isLoggingOut = false;
    private warningTimerId: ReturnType<typeof setInterval> | null = null;
    private subs = new Subscription();

    constructor(private idle: Idle,
        private keepalive: Keepalive,
        private zone: NgZone,
        private appRef: ApplicationRef,
        private router: Router,
        private _global: PortalGlobals
    ) {
        this.syncRootInsCoAttribute()
        this.redirectRootToLoginWhenNeeded()

        this.zone.run(() => {
            this.idle.setIdle(this.WARNING_AFTER_SECONDS);
            this.idle.setTimeout(this.WARNING_COUNTDOWN_SECONDS);
            this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

            this.subs.add(
                this.idle.onIdleStart.subscribe(() => {
                    // Only warn on authenticated (non-public) routes. Tracking is
                    // enabled/disabled per route by updateIdleTrackingForCurrentContext.
                    if (!this.idleTrackingEnabled) {
                        return;
                    }
                    // Show the warning and freeze interrupts so mouse/keyboard activity
                    // cannot dismiss it or reset the countdown (only Yes/No may act).
                    // ng-idle keeps running its timeout using wall-clock expiry, so the
                    // auto-logout still fires reliably even when the tab is backgrounded
                    // and timers are throttled.
                    this.showModal = true;
                    this.countdown = this.WARNING_COUNTDOWN_SECONDS;
                    this.idle.setInterrupts([]);
                })
            );

            this.subs.add(
                this.idle.onTimeoutWarning.subscribe((t: number) => {
                    // Drive the visible countdown from ng-idle while the warning is shown.
                    if (this.showModal) {
                        this.countdown = t;
                    }
                })
            );

            this.subs.add(
                this.idle.onIdleEnd.subscribe(() => {
                    // Keep the warning visible until an explicit Yes/No user action.
                    if (this.showModal) {
                        return;
                    }
                    this.updateIdleTrackingForCurrentContext();
                })
            );

            this.subs.add(
                this.idle.onTimeout.subscribe(() => {
                    // Countdown expired with no selection: force logout.
                    this.zone.run(() => this.onLogout());
                })
            );

            this.keepalive.interval(150);
            this.updateIdleTrackingForCurrentContext();
        });


        this.subs.add(
            this.router.events
                .pipe(filter(e => e instanceof NavigationEnd))
                .subscribe(() => {
                    this.syncRootInsCoAttribute()
                    this.zone.run(() => this.updateIdleTrackingForCurrentContext())
                    this.redirectRootToLoginWhenNeeded()
                })
        );

        this.subs.add(
            this.router.events
                .pipe(filter(e => e instanceof NavigationError))
                .subscribe((event) => {
                    const navigationError = event as NavigationError
                    const errorMessage = (navigationError.error && navigationError.error.message) ? navigationError.error.message : ''
                    const errorName = (navigationError.error && navigationError.error.name) ? navigationError.error.name : ''
                    const isUnmatchedRoute =
                        errorMessage.includes('NG04002') ||
                        errorMessage.includes('Cannot match any routes') ||
                        errorName.includes('NG04002')

                    if (!isUnmatchedRoute) {
                        return
                    }

                    const insCo = this.resolveInsCoForRecovery(navigationError.url)
                    const targetUrl = '/login/' + insCo

                    console.warn('Router NG04002 recovery: navigating to', targetUrl, 'after failed url', navigationError.url)

                    this.zone.run(() => {
                        this.syncRootInsCoAttribute(insCo)
                        this.router.navigateByUrl(targetUrl, { replaceUrl: true }).catch(() => {
                            window.location.replace('/ep-ui/#/login/' + encodeURIComponent(insCo))
                        })
                    })
                })
        )
    }

    private syncRootInsCoAttribute(forceInsCo?: string): void {
        const resolvedInsCo = (forceInsCo || this.resolveInsCoFromCurrentLocation()).toLowerCase()
        if (!resolvedInsCo) {
            return
        }

        document.documentElement.setAttribute('data-insco', resolvedInsCo)
        if (document.body) {
            document.body.setAttribute('data-insco', resolvedInsCo)
        }
    }

    private resolveInsCoFromCurrentLocation(): string {
        const hash = (window.location.hash || '').toLowerCase()

        if (hash.startsWith('#/login/neje') || hash.startsWith('#/neje/')) {
            return 'neje'
        }

        if (hash.startsWith('#/login/ssi') || hash.startsWith('#/ssi/')) {
            return 'ssi'
        }

        const fromSession = this.getInsCoFromUserSessionContext()
        if (fromSession) {
            return fromSession.toLowerCase()
        }

        const fromCookie = this.getCookieValue('insCo') || this.getCookieValue('insCoName')
        if (fromCookie) {
            return fromCookie.toLowerCase()
        }

        return 'ssi'
    }

    onStay() {
        this.zone.run(() => {
            this.isLoggingOut = false;
            this.clearWarningCountdown();
            this.closeModal();
            this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
            this.idle.watch();
            this.updateIdleTrackingForCurrentContext();
        });
    }

    onLogout() {
        const logoutUrl = this.buildLogoutUrl();

        if (this.isLoggingOut) {
            this.navigateToLogout(logoutUrl);
            return;
        }

        this.isLoggingOut = true;
        this.clearWarningCountdown();
        this.idleTrackingEnabled = false;
        this.idle.stop();
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.closeModal();
        this.navigateToLogout(logoutUrl);
    }

    ngOnDestroy() {
        this.clearWarningCountdown();
        this.subs.unsubscribe();
        this.idle.stop();
    }

    private closeModal() {
        this.showModal = false;
        this.countdown = 0;
    }

    private clearWarningCountdown() {
        if (this.warningTimerId) {
            clearInterval(this.warningTimerId);
            this.warningTimerId = null;
        }
    }

    private buildLogoutUrl(): string {
        let incName = 'ssi';
        const currentInsurer = this._global.currentUser?.insuranceCompanyName;

        if (currentInsurer === this._global.insuranceCompanyNameSSI) {
            incName = 'ssi';
        } else if (currentInsurer === this._global.insuranceCompanyNameNEJE) {
            incName = 'neje';
        } else {
            const fromSession = this.getInsCoFromUserSessionContext();
            const fromCookie = this.getCookieValue('insCo') || this.getCookieValue('insCoName');
            incName = (fromSession || fromCookie || 'ssi').toLowerCase();
        }

        return '/Auth/Logout.aspx?insCo=' + incName;
    }

    private navigateToLogout(logoutUrl: string): void {
        // Match the master branch: clear client storage and perform a synchronous
        // full-page redirect to the server logout page. A synchronous redirect
        // always runs immediately, even after long inactivity when the tab is
        // backgrounded and timers / animation frames are throttled or paused
        // (which is why a deferred navigation froze on the 30-minute auto-logout).
        // The warning overlay has already been closed by the caller (onLogout).
        try { sessionStorage.clear(); } catch { /* ignore */ }
        try { localStorage.clear(); } catch { /* ignore */ }

        window.location.href = logoutUrl;
    }

    private redirectRootToLoginWhenNeeded() {
        const hash = (window.location.hash || '').toLowerCase()

        if (hash === '#/login' || hash.startsWith('#/login/')) {
            return
        }

        const isRootHash = hash === '' || hash === '#' || hash === '#/' || hash.startsWith('#/?')

        if (!isRootHash) {
            return
        }

        const hasUserSessionContext = !!localStorage.getItem('UserSessionContext')

        const hasAuthCookie = !!this.getCookieValue('.ASPXAUTH-PRESENT')

        if (hasUserSessionContext && hasAuthCookie) {
            const insCoFromSession = this.getInsCoFromUserSessionContext()
            const insCo = (insCoFromSession || this.getCookieValue('insCo') || this.getCookieValue('insCoName') || 'ssi').toLowerCase()
            window.location.replace('/ep-ui/#/' + encodeURIComponent(insCo) + '/home')
            return
        }


        if (hasUserSessionContext && !hasAuthCookie) {
            try { localStorage.clear() } catch { /* ignore */ }
            try { sessionStorage.clear() } catch { /* ignore */ }
        }

        // No active session: always land on insurer picker so user can choose SSI or NEJE.
        window.location.replace('/ep-ui/#/login')
    }

    private getInsCoFromUserSessionContext(): string | null {
        try {
            const userSessionContext = localStorage.getItem('UserSessionContext')
            if (!userSessionContext) {
                return null
            }

            const parsedContext = JSON.parse(userSessionContext)
            const shortName = parsedContext?.insuranceCompanyShortName || parsedContext?.insuranceCompanyName

            if (!shortName || typeof shortName !== 'string') {
                return null
            }

            return shortName
        } catch {
            return null
        }
    }

    private getCookieValue(name: string): string | null {
        const value = '; ' + document.cookie
        const parts = value.split('; ' + name + '=')
        if (parts.length === 2) {
            return parts.pop()!.split(';').shift() || null
        }
        return null
    }

    private resolveInsCoForRecovery(failedUrl: string): string {
        const lowerFailedUrl = (failedUrl || '').toLowerCase()

        if (lowerFailedUrl.includes('/login/neje') || lowerFailedUrl.includes('/neje/')) {
            return 'neje'
        }

        if (lowerFailedUrl.includes('/login/ssi') || lowerFailedUrl.includes('/ssi/')) {
            return 'ssi'
        }

        const fromSession = this.getInsCoFromUserSessionContext()
        if (fromSession) {
            return fromSession.toLowerCase()
        }

        const fromCookie = this.getCookieValue('insCo') || this.getCookieValue('insCoName')
        if (fromCookie) {
            return fromCookie.toLowerCase()
        }

        return 'ssi'
    }

    private updateIdleTrackingForCurrentContext() {
        const shouldEnable = this.shouldEnableSessionWarning();

        if (shouldEnable) {
            this.idleTrackingEnabled = true;
            this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
            this.idle.watch();
            return;
        }

        this.clearWarningCountdown();
        this.idleTrackingEnabled = false;
        this.closeModal();
        this.idle.stop();
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    }

    private shouldEnableSessionWarning(): boolean {
        const hash = (window.location.hash || '').toLowerCase();
        const isPublicRoute =
            hash === '' ||
            hash === '#' ||
            hash === '#/' ||
            hash.startsWith('#/login') ||
            hash.startsWith('#/logout') ||
            hash.startsWith('#/contact-us') ||
            hash.startsWith('#/supported-browsers') ||
            hash.startsWith('#/forgot-password') ||
            hash.startsWith('#/reset-password') ||
            hash.startsWith('#/change-expired-password') ||
            hash.startsWith('#/migrate-user') ||
            hash.startsWith('#/change-user-login') ||
            hash.startsWith('#/sso') ||
            hash.startsWith('#/external-login') ||
            hash.startsWith('#/app-error') ||
            hash.startsWith('#/download-helper');

        // Enable the session warning on every authenticated (non-public) route,
        // regardless of insurer (SSI or NEJE). Do NOT gate on parsed session
        // context: those fields vary between portals/environments and were the
        // reason the warning never appeared for NEJE.
        return !isPublicRoute;
    }
}